import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { deliveryOrdersApi } from "@/services/salesDocumentsApi";
import type { CreateDeliveryOrderRequest } from "@/types/salesDocuments";

// Query key factory
const deliveryOrderKeys = {
  all: ["deliveryOrders"] as const,
  lists: () => [...deliveryOrderKeys.all, "list"] as const,
  list: (filters?: any) => [...deliveryOrderKeys.lists(), filters] as const,
  details: () => [...deliveryOrderKeys.all, "detail"] as const,
  detail: (id: string) => [...deliveryOrderKeys.details(), id] as const,
};

// Get all delivery orders
export const useDeliveryOrders = (filters?: {
  search?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  salesOrderId?: string;
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: deliveryOrderKeys.list(filters),
    queryFn: () => deliveryOrdersApi.getAll(filters),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

// Get single delivery order
export const useDeliveryOrder = (id: string | undefined) => {
  return useQuery({
    queryKey: deliveryOrderKeys.detail(id || ""),
    queryFn: () => deliveryOrdersApi.getById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

// Create delivery order
export const useCreateDeliveryOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDeliveryOrderRequest) =>
      deliveryOrdersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deliveryOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["salesOrders"] });
    },
  });
};

// Update delivery order
export const useUpdateDeliveryOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateDeliveryOrderRequest>;
    }) => deliveryOrdersApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: deliveryOrderKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: deliveryOrderKeys.detail(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: ["salesOrders"] });
    },
  });
};

// Delete delivery order
export const useDeleteDeliveryOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deliveryOrdersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deliveryOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["salesOrders"] });
    },
  });
};

// Update delivery order status
export const useUpdateDeliveryOrderStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      deliveryOrdersApi.updateStatus(id, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: deliveryOrderKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: deliveryOrderKeys.detail(variables.id),
      });
    },
  });
};

// Clone delivery order
export const useCloneDeliveryOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deliveryOrdersApi.clone(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deliveryOrderKeys.lists() });
    },
  });
};

// Send delivery order emails to buyers
export const useSendDeliveryOrderEmail = () => {
  return useMutation({
    mutationFn: (ids: string[]) => deliveryOrdersApi.sendEmail(ids),
  });
};

// Hook to use all delivery order operations
export const useDeliveryOrderOperations = () => {
  const createDeliveryOrder = useCreateDeliveryOrder();
  const updateDeliveryOrder = useUpdateDeliveryOrder();
  const deleteDeliveryOrder = useDeleteDeliveryOrder();
  const updateStatus = useUpdateDeliveryOrderStatus();
  const cloneDeliveryOrder = useCloneDeliveryOrder();

  return {
    createDeliveryOrder,
    updateDeliveryOrder,
    deleteDeliveryOrder,
    updateStatus,
    cloneDeliveryOrder,
    isLoading:
      createDeliveryOrder.isPending ||
      updateDeliveryOrder.isPending ||
      deleteDeliveryOrder.isPending ||
      updateStatus.isPending ||
      cloneDeliveryOrder.isPending,
  };
};
